/**
 * LanguageSection — two stacked sudokit MenuSelects (Language + Region)
 * that compose into LanguageTool's `language` parameter.
 *
 *   Language=English, Region=United States  →  en-US
 *   Language=German,  Region=Austria        →  de-AT
 *   Language=Russian (no regions)           →  ru
 *
 * The language list mirrors LanguageTool's `/v2/languages` response,
 * cleaned up so labels read naturally ("Australia", not "English
 * (Australian)"). For languages LT exposes with no region variants we
 * hide the Region picker entirely.
 */

import React, { useEffect } from "react";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { getCurrentEditor } from "src/lib/Editor";

interface RegionDef {
  code: string; // value sent to LanguageTool, e.g. "en-US"
  label: string; // display label, e.g. "United States"
}

interface LanguageDef {
  code: string; // base LT code, e.g. "en"
  label: string;
  /** value to send to LT when no region is selected. Falls back to the first region. */
  defaultLongCode?: string;
  regions: RegionDef[];
}

/**
 * Sorted alphabetically by display label. `defaultLongCode` is what we
 * send when the language is selected without an explicit region — for
 * region-less languages this is the bare base code (e.g. "ru").
 */
const LANGUAGES: LanguageDef[] = [
  { code: "ar", label: "Arabic", defaultLongCode: "ar", regions: [] },
  { code: "ast", label: "Asturian", defaultLongCode: "ast", regions: [] },
  { code: "be", label: "Belarusian", defaultLongCode: "be", regions: [] },
  { code: "br", label: "Breton", defaultLongCode: "br", regions: [] },
  {
    code: "ca",
    label: "Catalan",
    defaultLongCode: "ca",
    regions: [
      // "Standard" sends the bare base code so the picker's selection
      // always exact-matches the stored preference (avoids the
      // UI/storage mismatch bugbot caught when defaultLongCode was the
      // bare base but the regions list only had specific variants).
      { code: "ca", label: "Standard" },
      { code: "ca-ES-valencia", label: "Valencian" },
      { code: "ca-ES-balear", label: "Balearic" },
    ],
  },
  { code: "zh", label: "Chinese", defaultLongCode: "zh", regions: [] },
  { code: "crh", label: "Crimean Tatar", defaultLongCode: "crh", regions: [] },
  { code: "da", label: "Danish", defaultLongCode: "da", regions: [] },
  {
    code: "nl",
    label: "Dutch",
    defaultLongCode: "nl-NL",
    regions: [
      { code: "nl-NL", label: "Netherlands" },
      { code: "nl-BE", label: "Belgium" },
    ],
  },
  {
    code: "en",
    label: "English",
    defaultLongCode: "en-US",
    regions: [
      { code: "en-US", label: "United States" },
      { code: "en-GB", label: "United Kingdom" },
      { code: "en-AU", label: "Australia" },
      { code: "en-CA", label: "Canada" },
      { code: "en-NZ", label: "New Zealand" },
      { code: "en-ZA", label: "South Africa" },
    ],
  },
  { code: "eo", label: "Esperanto", defaultLongCode: "eo", regions: [] },
  {
    code: "fr",
    label: "French",
    defaultLongCode: "fr-FR",
    regions: [
      { code: "fr-FR", label: "France" },
      { code: "fr-CA", label: "Canada" },
      { code: "fr-CH", label: "Switzerland" },
      { code: "fr-BE", label: "Belgium" },
    ],
  },
  { code: "gl", label: "Galician", defaultLongCode: "gl", regions: [] },
  {
    code: "de",
    label: "German",
    defaultLongCode: "de-DE",
    regions: [
      { code: "de-DE", label: "Germany" },
      { code: "de-AT", label: "Austria" },
      { code: "de-CH", label: "Switzerland" },
    ],
  },
  { code: "el", label: "Greek", defaultLongCode: "el", regions: [] },
  { code: "ga", label: "Irish", defaultLongCode: "ga", regions: [] },
  { code: "it", label: "Italian", defaultLongCode: "it", regions: [] },
  { code: "ja", label: "Japanese", defaultLongCode: "ja", regions: [] },
  { code: "km", label: "Khmer", defaultLongCode: "km", regions: [] },
  {
    code: "fa",
    label: "Persian",
    defaultLongCode: "fa",
    regions: [],
  },
  { code: "pl", label: "Polish", defaultLongCode: "pl", regions: [] },
  {
    code: "pt",
    label: "Portuguese",
    defaultLongCode: "pt-PT",
    regions: [
      { code: "pt-PT", label: "Portugal" },
      { code: "pt-BR", label: "Brazil" },
      { code: "pt-AO", label: "Angola" },
      { code: "pt-MZ", label: "Mozambique" },
    ],
  },
  { code: "ro", label: "Romanian", defaultLongCode: "ro", regions: [] },
  { code: "ru", label: "Russian", defaultLongCode: "ru", regions: [] },
  { code: "sk", label: "Slovak", defaultLongCode: "sk", regions: [] },
  { code: "sl", label: "Slovenian", defaultLongCode: "sl", regions: [] },
  {
    code: "es",
    label: "Spanish",
    defaultLongCode: "es",
    regions: [
      // See Catalan above — the bare base needs an explicit row so the
      // Region picker can show it as the selected value.
      { code: "es", label: "Standard" },
      { code: "es-AR", label: "Argentina (Voseo)" },
    ],
  },
  { code: "sv", label: "Swedish", defaultLongCode: "sv", regions: [] },
  { code: "tl", label: "Tagalog", defaultLongCode: "tl", regions: [] },
  { code: "ta", label: "Tamil", defaultLongCode: "ta", regions: [] },
  { code: "uk", label: "Ukrainian", defaultLongCode: "uk", regions: [] },
];

function findLanguage(code: string): LanguageDef {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES.find((l) => l.code === "en")!;
}

/**
 * Parse the stored preference string (e.g. "en-US", "ru", "ca-ES-valencia")
 * into the active language + region. `needsNormalization` is set when the
 * stored value didn't exactly match anything we recognize and we had to
 * fall back — the caller persists the resolved value so /check uses the
 * same locale the UI shows (otherwise "en-XX" would display as "United
 * States" but LT would keep getting called with "en-XX").
 */
function parsePreference(raw: string | undefined | null): {
  language: LanguageDef;
  regionCode: string | null;
  needsNormalization: boolean;
} {
  const value = (raw || "en-US").trim();
  const base = value.split("-")[0];
  const language = LANGUAGES.find((l) => l.code === base);

  if (!language) {
    // Stored value's base code isn't in our catalogue at all — reset to
    // English en-US and flag for normalization.
    return {
      language: findLanguage("en"),
      regionCode: "en-US",
      needsNormalization: true,
    };
  }

  // Region-less languages: the bare base code is the only valid value.
  if (language.regions.length === 0) {
    return {
      language,
      regionCode: null,
      needsNormalization: value !== base,
    };
  }

  // Exact region match (handles 3-segment codes like ca-ES-valencia,
  // and bare-base "Standard" rows for es/ca).
  const exact = language.regions.find((r) => r.code === value);
  if (exact) {
    return { language, regionCode: exact.code, needsNormalization: false };
  }

  // Recognized base, unrecognized region (e.g., "en-XX"). Fall back to
  // the language's default and persist the resolved code.
  const fallback =
    language.regions.find((r) => r.code === language.defaultLongCode) ??
    language.regions[0];
  return {
    language,
    regionCode: fallback.code,
    needsNormalization: true,
  };
}

export function LanguageSection() {
  const editorOptions = useSudowriteStore((s: any) => s.editorOptions) || {};
  const setEditorOptions = useSudowriteStore((s: any) => s.setEditorOptions);
  const { language, regionCode, needsNormalization } = parsePreference(
    editorOptions.languagePreference
  );

  const applyPreference = (next: string) => {
    setEditorOptions({ ...editorOptions, languagePreference: next });
    const editor = getCurrentEditor?.();
    if (editor?.commands?.setLanguagePreference) {
      editor.commands.setLanguagePreference(next);
    } else if (editor?.commands?.recheckGrammarFull) {
      editor.commands.recheckGrammarFull();
    }
  };

  // SW-9064 (bugbot): if the stored languagePreference doesn't exactly
  // match what the UI ended up displaying — invalid region, unknown
  // language, or a bare base for a language whose regions list didn't
  // include it — persist the resolved code so /check and the dialog
  // agree. Resolved code is always an exact-match entry so the next
  // parsePreference run won't trigger this again (no write loop).
  useEffect(() => {
    if (!needsNormalization) return;
    const resolved = regionCode ?? language.code;
    if (resolved && resolved !== editorOptions.languagePreference) {
      applyPreference(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsNormalization, regionCode, language.code]);

  const onLanguageChange = (nextLangCode: string) => {
    const next = findLanguage(nextLangCode);
    applyPreference(next.defaultLongCode ?? next.code);
  };

  const onRegionChange = (nextRegionCode: string) => {
    applyPreference(nextRegionCode);
  };

  const languageOptions = LANGUAGES.map((l) => ({ value: l.code, label: l.label }));
  const regionOptions = language.regions.map((r) => ({ value: r.code, label: r.label }));
  const regionLabel = language.regions.find((r) => r.code === regionCode)?.label ?? "";

  return (
    <section className="flex flex-col gap-5" data-section="language">
      <p className="m-0 text-sm text-app-grey-1 font-inter [text-wrap:pretty]">
        Spelling and grammar suggestions are tuned to the variant you pick here.
      </p>

      <MenuSelect
        options={languageOptions}
        value={language.code}
        label={language.label}
        variant="secondary"
        onChange={onLanguageChange}
        optionAlignment="left"
        menuAlign="start"
      />

      {regionOptions.length > 0 && regionCode && (
        <MenuSelect
          options={regionOptions}
          value={regionCode}
          label={regionLabel}
          variant="secondary"
          onChange={onRegionChange}
          optionAlignment="left"
          menuAlign="start"
        />
      )}
    </section>
  );
}
